<script>
  import { refreshAll } from '$app/navigation';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Dialog from '#lib/Dialog.svelte';
  import { fetch_json, format_date, localize_utc_date } from '#lib/util.js';
  import { offset_month, get_month_distance, get_last_day_of_month, format_price } from '#lib/patch_util.js';
  import { page } from '$app/state';
  import t from '#lib/i18n.svelte.js';
  import PriceInput from '#lib/PriceInput.svelte';
  import FormLabel from '#lib/FormLabel.svelte';
  import Switch from '#lib/Switch.svelte';

  let { data } = $props();

  const SUBSCRIPTION_CANCELLATION_WINDOW_SIZE = 24;

  let selected_end_year_and_month = $state();
  let selected_custom_price = $state(null);
  let interface_state = $state({ mode: 'view' });

  let selected_subscription = $derived.by(() => {
    return data.active_and_future_subscriptions.find(subscription => subscription.subscription_id == interface_state.subscription_id);
  });

  let selectable_end_months = $derived.by(() => {
    if (!selected_subscription) return [];

    // Business-local date, not raw UTC — they differ in the first hours of a
    // new local month, which would shift the selectable cancellation months
    const current_date = localize_utc_date(new Date());
    const current_year = current_date.getUTCFullYear();
    const current_month = current_date.getUTCMonth() + 1;

    // When in edit mode (only admins can edit), we allow for immediate cancellation, otherwise add notice period
    const notice_period = interface_state.mode === 'edit' ? 0 : data.notice_period;

    // The month used for reference (either the subscriptions start_year/start_month or the current year/month)
    let reference_year, reference_month, result = [];
    
    // If selected_subscription.start_year/selected_subscription.start_month is in the future, we use that as a reference
    // for earliest cancellation month.
    if (get_month_distance(current_year, current_month, selected_subscription.start_year, selected_subscription.start_month) > 0) {
      reference_year = selected_subscription.start_year;
      reference_month = selected_subscription.start_month;
    } else {
      reference_year = current_year;
      reference_month = current_month;
    }

    // Option 'no end date'
    result.push({ year: null, month: null });

    for (let i = notice_period; i <= SUBSCRIPTION_CANCELLATION_WINDOW_SIZE; i++) {
      const [ year, month ] = offset_month(reference_year, reference_month, i);
      result.push({
        year,
        month,
        is_selected: year === selected_subscription.end_year && month === selected_subscription.end_month
      });
    }

    return result;
  });

  async function cancel_subscription() {
    try {
      const [end_year, end_month] = __parse_year_month(selected_end_year_and_month);

      await fetch_json('POST', '/api/cancel-subscription', {
        subscription_id: interface_state.subscription_id,
        end_year,
        end_month
      });
      interface_state = { mode: 'view' };
      refreshAll();
    } catch(err) {
      alert('Das Abbonement konnte nicht storniert werden. Bitte wende dich an den Support.');
    }
  }

  async function uncancel_subscription(subscription_id) {
    try {
      await fetch_json('POST', '/api/uncancel-subscription', {
        subscription_id
      });
      interface_state = { mode: 'view' };
      refreshAll();
    } catch(err) {
      alert('Fehler beim Verlängern des Abonnements. Die gewünschte Resource ist aktuell ausgebucht.');
    }
  }

  async function update_subscription() {
    try {
      const [end_year, end_month] = __parse_year_month(selected_end_year_and_month);
      await fetch_json('POST', '/api/update-subscription', {
        subscription_id: interface_state.subscription_id,
        end_year,
        end_month,
        custom_price: selected_custom_price
      });
      interface_state = { mode: 'view' };
      refreshAll();
    } catch(err) {
      alert('Fehler beim Ändern des Abonnements. Die gewünschte Resource ist für diesen Zeitraum ausgebucht.');
    }
  }

  function onclose() {
    interface_state = { mode: 'view' };
    refreshAll();
  }

  function __parse_year_month(year_month) {
    if (!year_month) return [null, null];
    return [
      parseInt(year_month.split('-')[0], 10),
      parseInt(year_month.split('-')[1], 10)
    ]
  }
</script>

{#if data.active_and_future_subscriptions.length > 0 || data.expired_subscriptions.length > 0}  
  {#if data.active_and_future_subscriptions.length > 0}
    <!-- List currently active and future subscriptions (all that haven't ended before the current month -->
    <div class='flex-column'>
      <p class='text-preamble leading-trim'>{data.resource.title}</p>
      <h1 class='text-heading1 leading-trim py p-5'>{t('active_subscriptions')}</h1>
    </div>
    <div class='active-and-future-subscriptions flex-column'>
      {#each data.active_and_future_subscriptions as { 
          subscription_id, 
          start_year, 
          start_month, 
          end_year, 
          end_month, 
          user_email,
          user_name,
          cancelled_at,
          custom_price,
          custom_price_description
        }}
        <div 
          class='subscription-card flex-wrap gap-x gap-y g-4 items-start justify-between border-sm'
          class:is-editing={interface_state.mode === 'edit' && interface_state.subscription_id === subscription_id}
        >
          <div class='flex-column gap-y g-5 full-width'>
            <div class='flex-row gap-x g-4 items-center justify-between'>
              <p class='text-body leading-trim'>{t('subscription')} #{subscription_id}</p>
              {#if page.data.current_user?.role === 'admin'}
                <button onclick={() => {
                  interface_state = { mode: 'edit', subscription_id }; 
                  selected_custom_price = custom_price;  // Initialize with current custom_price (null or a number)
                  selected_end_year_and_month = (end_year && end_month) ? `${end_year}-${end_month}` : '';  // Initialize with current end date
                }}>
                  <ButtonLabel start_icon='pencil' variant='outlined'>{t('change')}</ButtonLabel>
                </button>
              {:else if !end_year && !end_month}
                <!-- NOTE: Cancel button is only shown if subscription hasn't been cancelled already -->
                <button onclick={() => interface_state = { mode: 'cancel', subscription_id}}>
                  <ButtonLabel variant='system-destructive outlined'>{t('cancel_subscription')}</ButtonLabel>
                </button>
              {:else}
                <button onclick={() => uncancel_subscription(subscription_id)}>
                  <ButtonLabel variant='outlined'>{t('uncancel_subscription')}</ButtonLabel>
                </button>
              {/if}
            </div>
            <div class='form-row-wide-label gap-x gap-y g-6'>
              <p class='text-body text-3 leading-trim'>{t('start')}:</p>
              <p class='text-body text-3 leading-trim text-3'>
                {format_date(`${start_year}-${start_month.toString().padStart(2, '0')}-01`, 'DMMMMY')}
                {#if !end_year && !end_month}
                  <span class='text-body text-3 leading-trim'>{t('renews_automatically')}</span>
                {/if}
              </p>
            </div>
            {#if end_year && end_month}
              <div class='form-row-wide-label gap-x gap-y g-6'>
                <p class='text-body text-3 leading-trim'>{t('end')}:</p>
                <p class='text-body text-3 leading-trim text-3'>{format_date(get_last_day_of_month(end_year, end_month), 'DMMMMY')}</p>
              </div>
              {#if cancelled_at}
                <div class='form-row-wide-label gap-x gap-y g-6'>
                  <p class='text-body text-3 leading-trim'>{t('cancelled_at')}:</p>
                  <p class='text-body text-3 leading-trim text-3'>{format_date(cancelled_at, 'DMMMMY')}</p>
                </div>
              {/if}
            {/if}
            <div class='form-row-wide-label gap-x gap-y g-6'>
              <p class='text-body text-3 leading-trim'>{t('price')}:</p>
              <p class='text-body text-3 leading-trim text-3'>
                {#if custom_price !== null && custom_price !== undefined}
                  <span class='regular-price -strike'>{format_price(data.pricing?.price)}</span>
                  <span class='custom-price'>{format_price(custom_price)}</span>
                {:else}
                  <span class='regular-price'>{format_price(data.pricing?.price)}</span>
                {/if} / {t('month')} <span class='text-3'>({t('plus_vat')})</span></p>
            </div>
            <div class='form-row-wide-label gap-x gap-y g-6'>
              <p class='text-body text-3 leading-trim'>{t('subscription_by')}:</p>
              <div class='flex-column gap-y g-4'>
                <p class='text-body text-3 leading-trim text-3'>{user_name}</p>
                <p class='text-body text-3 leading-trim text-3'>{user_email}</p>
              </div>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  {#if data.expired_subscriptions.length > 0}
    <h2 class='text-heading3 leading-trim mbs m-6 py p-5'>{t('expired_subscriptions')}</h2>
    <div class='expired-subscriptions flex-column'>
      {#each data.expired_subscriptions as { subscription_id, start_year, start_month, end_year, end_month, user_email, user_name }}
        <div class='subscription-card flex-column py p-4 gap-y g-4 py p-5'>
          <h3 class='text-body leading-trim'>{t('subscription')} #{subscription_id}</h3>
          <div class='flex-column gap-y g-4'>
            <div class='flex-row justify-between gap-x g-6'>
              <p class='text-body text-3 leading-trim'>{t('start')}:</p>
              <p class='text-body text-3 leading-trim'>{format_date(`${start_year}-${start_month.toString().padStart(2, '0')}-01`, 'DMMMMY')}</p>
            </div>
            <div class='flex-row justify-between gap-x g-6'>
              <p class='text-body text-3 leading-trim'>{t('end')}:</p>
              <p class='text-body text-3 leading-trim'>{format_date(get_last_day_of_month(end_year, end_month), 'DMMMMY')}</p>
            </div>
            <div class='flex-row justify-between gap-x g-6'>
              <p class='text-body text-3 leading-trim'>{t('subscription_by')}:</p>
              <div class='flex-column gap-y g-4'>
                <p class='text-body text-3 leading-trim'>{user_name}</p>
                <p class='text-body text-3 leading-trim'>{user_email}</p>
              </div>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
{/if}

{#snippet select_end_month()}
  <div class='form-row gap-x g-4 items-center'>
    <label class='text-control text-1' for='end_date'>{t('end_date')}:</label>
    <select id='end_date' bind:value={selected_end_year_and_month}>
      <option disabled>{t('select_end_date')}</option>

       <!-- "no end date" option, so subscriptions can run indefinitely -->
       <option value='' selected={!selected_subscription.end_year && !selected_subscription.end_month}>
        {t('no_end_date')}
      </option>
      
      {#each [...new Set(selectable_end_months.filter(m => m.year).map(m => m.year))] as groupYear}
        <optgroup label={groupYear}>
          {#each selectable_end_months.filter(m => m.year === groupYear) as { year, month, is_selected }}
            <option 
              value={`${year}-${month}`} 
              selected={is_selected}
            >
              {format_date(`${year}-${month.toString().padStart(2, '0')}-01`, 'MMMM')}
            </option>
          {/each}
        </optgroup>
      {/each}
    </select>
  </div>
{/snippet}

<!-- NOTE: only normal users get to see the cancellation UI, for admins we have a dedicated edit view instead -->
{#if interface_state.mode === 'cancel'}
  <Dialog 
    {onclose}
    class='wide'
  >
    <div class='cancel-dialog'>
      <div class='flex-column px py p-6 gap-y g-4'>
        <h3 class='text-heading3 leading-trim'>{(t('cancel_subscription_with_id', { subscription: selected_subscription.subscription_id }))}</h3>
        <div class='flex-row gap-x g-4 items-center'>
          <p class='text-body'>{t('start_date')}:</p>
          <p>{format_date(`${selected_subscription.start_year}-${selected_subscription.start_month.toString().padStart(2, '0')}-01`, 'MMMMY')}</p>
        </div>
        {@render select_end_month()}
      </div>
      <div class='flex-row justify-end gap-x g-4 px py p-6 bg-2'>
        <button onclick={onclose}>
          <ButtonLabel variant='brand outlined'>{t('cancel')}</ButtonLabel>
        </button>
        <button onclick={cancel_subscription}>
          <ButtonLabel variant='brand encased'>{t('cancel_subscription')}</ButtonLabel>
        </button>
      </div>
    </div>
  </Dialog>
{/if}


{#if interface_state.mode === 'edit'}
  <Dialog 
    {onclose}
    class='wide border-sm'
  >
    <div class='edit-dialog'>
      <div class='flex-column px py p-6 gap-y g-4'>
        <h3 class='text-heading3 leading-trim mbe m-4'>{(t('change_subscription_with_id', { subscription: selected_subscription.subscription_id }))}</h3>
        <div class='form-row gap-x g-4 items-center'>
          <p class='text-control text-1'>{t('start_date')}:</p>
          <p class='text-control text-1'>{format_date(`${selected_subscription.start_year}-${selected_subscription.start_month.toString().padStart(2, '0')}-01`, 'MMMMY')}</p>
        </div>
        {@render select_end_month()}
        {#if page.data.current_user.role === 'admin'}
          <div class='form-row gap-x g-4 items-center'>
            <label class='text-control text-1' for='custom_price'>{t('custom_price')}:</label>
            <div class='flex-row gap-x g-4 items-center'>
              <Switch 
                value={selected_custom_price !== null}
                toggle={(use_custom_price) => {
                  selected_custom_price = use_custom_price ? (selected_custom_price ?? data.pricing?.price) : null;
                }} />
              {#if selected_custom_price !== null}
                <PriceInput 
                  id='custom_price' 
                  bind:value={selected_custom_price} 
                  placeholder={format_price(data.pricing?.price)} 
                  allow_zero={true} />
              {/if}
            </div>
          </div>
        {/if}
      </div>
      <div class='flex-row justify-end gap-x g-4 px py p-6 bg-2'>
        <button onclick={onclose}>
          <ButtonLabel variant='brand outlined'>{t('cancel')}</ButtonLabel>
        </button>
        <button onclick={update_subscription}>
          <ButtonLabel variant='brand encased'>{t('save')}</ButtonLabel>
        </button>
      </div>
    </div>
  </Dialog>
{/if}

<style lang='postcss'>
  .subscription-card {
    padding-block-end: var(--size-4);
    margin-block: var(--size-4);
    @media not (hover: hover) {
      border-top: 1px solid var(--color-default-background);
    }
  }
  .active-and-future-subscriptions .subscription-card {
    transition: all 0.2s ease-in-out;
    box-shadow: 0 0 0 0 transparent;
    @media (hover: hover) {
      &:hover {
        background: var(--color-default-emphasis-background);
        box-shadow: 0 0 0 var(--size-4) var(--color-default-emphasis-background);
      }
    }
    &.is-editing {
      outline: 1px solid var(--color-default-foreground);
      outline-offset: var(--size-4);
      transition: none;
    }
  }
</style>